import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  Palette as PaletteIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  CalendarToday as CalendarIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import apiService from '../services/api';

interface DesignerStats {
  designerId: string;
  designerName: string;
  designerEmail?: string;
  totalArts: number;
  externalArts?: number;
  totalOrders: number;
  finalizedOrders: number;
  avgArtsPerOrder: number;
  artsPerDay: number;
  rank: number;
}

const periodChartTitle: Record<string, string> = {
  'hoje': 'Produtividade de Hoje',
  'semana-atual': 'Evolução por Dia (Semana Atual)',
  'mes-atual': 'Evolução por Semana (Mês Atual)',
  'mes-passado': 'Evolução por Semana (Mês Passado)',
  'trimestre': 'Evolução por Mês (Trimestre)',
};

const DesignerProductivity: React.FC = () => {
  const [period, setPeriod] = useState('mes-atual');
  const [designersData, setDesignersData] = useState<DesignerStats[]>([]);
  const [weeklyBreakdown, setWeeklyBreakdown] = useState<Array<Record<string, number | string>>>([]);
  const [monthlyHistory, setMonthlyHistory] = useState<Array<{ month: string; total: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [productivityResult, historyData] = await Promise.all([
          apiService.getDesignerProductivity(period),
          apiService.getMonthlyArtsHistory(5),
        ]);
        const result = productivityResult as any;
        setDesignersData(result?.designers || []);
        setWeeklyBreakdown(result?.weeklyBreakdown || []);
        setMonthlyHistory(historyData || []);
      } catch (err: any) {
        console.error('Erro ao carregar produtividade:', err);
        setError(err.message || 'Erro ao carregar dados de produtividade');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [period]);

  // Gerar iniciais do nome para avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Dados reais de evolução vindos do backend (por semana/dia/mês conforme período)
  const evolutionData = React.useMemo(() => {
    return weeklyBreakdown.length > 0 ? weeklyBreakdown : [];
  }, [weeklyBreakdown]);

  // Comparação mensal (dados reais do backend)
  const monthlyComparison = React.useMemo(() => {
    return monthlyHistory.length > 0 
      ? monthlyHistory 
      : [{ month: 'Sem dados', total: 0 }];
  }, [monthlyHistory]);

  const totalArts = designersData.reduce((sum, d) => sum + d.totalArts, 0);
  const totalOrders = designersData.reduce((sum, d) => sum + d.totalOrders, 0);
  const avgArtsPerOrder = totalOrders > 0 ? totalArts / totalOrders : 0;

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1: return '#fbbf24';
      case 2: return '#9ca3af';
      case 3: return '#cd7f32';
      default: return '#64748b';
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `${rank}º`;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            📊 Dashboard de Produtividade - Designers
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Acompanhe métricas e desempenho da equipe de design
          </Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            sx={{ backgroundColor: 'white' }}
          >
            <MenuItem value="hoje">Hoje</MenuItem>
            <MenuItem value="semana-atual">Semana Atual</MenuItem>
            <MenuItem value="mes-atual">Mês Atual</MenuItem>
            <MenuItem value="mes-passado">Mês Passado</MenuItem>
            <MenuItem value="trimestre">Último Trimestre</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Cards de Resumo Geral */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PaletteIcon sx={{ color: '#3b82f6' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                    Total de Artes
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {totalArts || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#d1fae5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BarChartIcon sx={{ color: '#10b981' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                    Total de Pedidos
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {totalOrders || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#fef3c7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SpeedIcon sx={{ color: '#f59e0b' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                    Média por Pedido
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {avgArtsPerOrder.toFixed(1)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* Gráficos */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Evolução Semanal */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                📈 {periodChartTitle[period] ?? 'Evolução de Produtividade'}
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={evolutionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="period" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip />
                  <Legend />
                  {designersData.slice(0, 4).map((designer, index) => {
                    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];
                    const key = designer.designerName.toLowerCase().replace(/\s+/g, '');
                    return (
                      <Bar 
                        key={designer.designerId} 
                        dataKey={key} 
                        name={designer.designerName} 
                        fill={colors[index % colors.length]} 
                        radius={[8, 8, 0, 0]} 
                      />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Comparação Mensal */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                📊 Total Mensal
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyComparison}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip />
                  <Bar dataKey="total" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Ranking e Detalhes dos Designers */}
      <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <TrophyIcon sx={{ color: '#fbbf24', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Ranking de Produtividade
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Designer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total Artes</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Externas</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Pedidos</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Média/Pedido</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Artes/Dia (média)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Performance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {designersData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Nenhum dado de produtividade encontrado para o período selecionado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  designersData.map((designer) => (
                    <TableRow key={designer.designerId} hover>
                      <TableCell>
                        <Chip
                          label={getRankIcon(designer.rank)}
                          size="small"
                          sx={{
                            backgroundColor: `${getRankColor(designer.rank)}20`,
                            color: getRankColor(designer.rank),
                            fontWeight: 700,
                            fontSize: '14px',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              backgroundColor: getRankColor(designer.rank),
                              width: 36,
                              height: 36,
                              fontSize: '14px',
                              fontWeight: 600,
                            }}
                          >
                            {getInitials(designer.designerName)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {designer.designerName}
                          </Typography>
                        </Box>
                      </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PaletteIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                          {designer.totalArts}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {(designer.externalArts ?? 0) > 0 ? (
                        <Chip
                          label={designer.externalArts}
                          size="small"
                          sx={{ backgroundColor: '#fef3c7', color: '#b45309', fontWeight: 700 }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{designer.totalOrders}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={designer.avgArtsPerOrder.toFixed(1)}
                        size="small"
                        sx={{
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {designer.artsPerDay.toFixed(1)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={designersData.length > 0 && designersData[0].totalArts > 0 
                            ? (designer.totalArts / designersData[0].totalArts) * 100 
                            : 0}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#e5e7eb',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              backgroundColor: getRankColor(designer.rank),
                            },
                          }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                          {designersData.length > 0 && designersData[0].totalArts > 0
                            ? ((designer.totalArts / designersData[0].totalArts) * 100).toFixed(0)
                            : 0}%
                        </Typography>
                      </Box>
                    </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DesignerProductivity;

